import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type PackageStatus = Database['public']['Enums']['package_status_enum'];
export type PackageRow = Database['public']['Tables']['packages']['Row'];
export type PackageInsert = Database['public']['Tables']['packages']['Insert'];
export type PackageUpdate = Database['public']['Tables']['packages']['Update'];
export type PackageItemRow = Database['public']['Tables']['package_items']['Row'];
export type PackageItemInsert = Database['public']['Tables']['package_items']['Insert'];
export type PackageItemUpdate = Database['public']['Tables']['package_items']['Update'];
export type ProductRow = Database['public']['Tables']['products']['Row'];
export type CustomerRow = Database['public']['Tables']['customers']['Row'];

// Extended types for UI
export interface PackageWithDetails extends PackageRow {
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
  };
  package_items?: Array<PackageItemRow & {
    product?: ProductRow;
  }>;
}

export interface PackageListItem {
  id: string;
  title: string;
  customer_name: string;
  customer_phone: string;
  item_count: number;
  total: number;
  status: PackageStatus;
  created_at: string;
  updated_at: string;
}

// Package CRUD operations
export const packageService = {
  // Get all packages for the company
  async getPackages(companyId: string): Promise<PackageListItem[]> {
    const { data, error } = await supabase
      .from('packages')
      .select(`
        id,
        title,
        status,
        total_net,
        created_at,
        package_datetime,
        customer:customers(
          id,
          first_name,
          last_name,
          phone
        ),
        package_items(count)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching packages:', error);
      throw error;
    }

    return data.map(pkg => ({
      id: pkg.id,
      title: pkg.title || `Package ${pkg.id.slice(0, 8)}`,
      customer_name: pkg.customer ? 
        `${pkg.customer.first_name || ''} ${pkg.customer.last_name || ''}`.trim() || 'Unknown Customer' :
        'No Customer',
      customer_phone: pkg.customer?.phone || '',
      item_count: Array.isArray(pkg.package_items) ? pkg.package_items.length : 0,
      total: pkg.total_net,
      status: pkg.status,
      created_at: pkg.created_at,
      updated_at: pkg.package_datetime,
    }));
  },

  // Get a single package with full details
  async getPackageById(packageId: string, companyId: string): Promise<PackageWithDetails | null> {
    const { data, error } = await supabase
      .from('packages')
      .select(`
        *,
        customer:customers(
          id,
          first_name,
          last_name,
          phone
        ),
        package_items(
          *,
          product:products(*)
        )
      `)
      .eq('id', packageId)
      .eq('company_id', companyId)
      .single();

    if (error) {
      console.error('Error fetching package:', error);
      throw error;
    }

    return data;
  },

  // Create a new package
  async createPackage(packageData: PackageInsert): Promise<string> {
    const { data, error } = await supabase
      .from('packages')
      .insert(packageData)
      .select('id')
      .single();

    if (error) {
      console.error('Error creating package:', error);
      throw error;
    }

    return data.id;
  },

  // Update an existing package
  async updatePackage(packageId: string, updates: PackageUpdate): Promise<void> {
    const { error } = await supabase
      .from('packages')
      .update(updates)
      .eq('id', packageId);

    if (error) {
      console.error('Error updating package:', error);
      throw error;
    }
  },

  // Delete a package
  async deletePackage(packageId: string): Promise<void> {
    const { error } = await supabase
      .from('packages')
      .delete()
      .eq('id', packageId);

    if (error) {
      console.error('Error deleting package:', error);
      throw error;
    }
  },

  // Duplicate a package
  async duplicatePackage(packageId: string, companyId: string): Promise<string> {
    // First get the original package and its items
    const originalPackage = await this.getPackageById(packageId, companyId);
    if (!originalPackage) {
      throw new Error('Package not found');
    }

    // Create new package
    const newPackageData: PackageInsert = {
      company_id: originalPackage.company_id,
      customer_id: originalPackage.customer_id,
      title: `${originalPackage.title} (Copy)`,
      note: originalPackage.note,
      total_discount: originalPackage.total_discount,
      total_gross: originalPackage.total_gross,
      total_net: originalPackage.total_net,
      status: 'draft', // Always create duplicates as draft
      package_datetime: new Date().toISOString(),
    };

    const newPackageId = await this.createPackage(newPackageData);

    // Copy all package items
    if (originalPackage.package_items && originalPackage.package_items.length > 0) {
      const itemsToInsert: PackageItemInsert[] = originalPackage.package_items.map(item => ({
        company_id: item.company_id,
        package_id: newPackageId,
        product_id: item.product_id,
        quantity: item.quantity,
        quoted_price_each: item.quoted_price_each,
        discount: item.discount,
        line_total_gross: item.line_total_gross,
        line_total_net: item.line_total_net,
        cost_each_snapshot: item.cost_each_snapshot,
      }));

              await packageItemService.addPackageItems(itemsToInsert);
    }

    return newPackageId;
  },

  // Get package status statistics
  async getPackageStats(companyId: string): Promise<{
    draft: number;
    offered: number;
    accepted: number;
    rejected: number;
    expired: number;
    converted: number;
    cancelled: number;
    totalValue: number;
  }> {
    const { data, error } = await supabase
      .from('packages')
      .select('status, total_net')
      .eq('company_id', companyId);

    if (error) {
      console.error('Error fetching package stats:', error);
      throw error;
    }

    const stats = {
      draft: 0,
      offered: 0,
      accepted: 0,
      rejected: 0,
      expired: 0,
      converted: 0,
      cancelled: 0,
      totalValue: 0,
    };

    data.forEach(pkg => {
      stats[pkg.status]++;
      stats.totalValue += pkg.total_net;
    });

    return stats;
  },
};

// Package Items operations
export const packageItemService = {
  // Add multiple package items
  async addPackageItems(items: PackageItemInsert[]): Promise<void> {
    const { error } = await supabase
      .from('package_items')
      .insert(items);

    if (error) {
      console.error('Error adding package items:', error);
      throw error;
    }
  },

  // Update a package item
  async updatePackageItem(itemId: string, updates: PackageItemUpdate): Promise<void> {
    const { error } = await supabase
      .from('package_items')
      .update(updates)
      .eq('id', itemId);

    if (error) {
      console.error('Error updating package item:', error);
      throw error;
    }
  },

  // Delete a package item
  async deletePackageItem(itemId: string): Promise<void> {
    const { error } = await supabase
      .from('package_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('Error deleting package item:', error);
      throw error;
    }
  },

  // Get items for a specific package
  async getPackageItems(packageId: string): Promise<Array<PackageItemRow & { product?: ProductRow }>> {
    const { data, error } = await supabase
      .from('package_items')
      .select(`
        *,
        product:products(*)
      `)
      .eq('package_id', packageId);

    if (error) {
      console.error('Error fetching package items:', error);
      throw error;
    }

    return data;
  },

  // Synchronize package items - handles adding, updating, and removing items
  async syncPackageItems(
    packageId: string, 
    companyId: string, 
    currentItems: Array<{
      id?: string;
      productId: string;
      quantity: number;
      price: number;
    }>
  ): Promise<void> {
    try {
      console.log('🔄 Starting syncPackageItems for package:', packageId);
      console.log('📝 Current items to sync:', currentItems);
      
      // Get existing items from database
      const existingItems = await this.getPackageItems(packageId);
      console.log('💾 Existing items from DB:', existingItems);
      
      // SIMPLE APPROACH: Delete all existing items and recreate them
      // This handles duplicates and ensures a clean state
      
      // Step 1: Delete ALL existing items for this package
      if (existingItems.length > 0) {
        console.log('🗑️ Deleting all existing items for clean state...');
        for (const item of existingItems) {
          try {
            console.log('🗑️ Deleting item:', item.id);
            await this.deletePackageItem(item.id);
            console.log('✅ Successfully deleted item:', item.id);
          } catch (error) {
            console.error('❌ Failed to delete item:', item.id, error);
            throw new Error(`Failed to delete package item ${item.id}: ${error.message}`);
          }
        }
      }
      
      // Step 2: Add all current items fresh
      if (currentItems.length > 0) {
        try {
          console.log('➕ Adding all current items fresh...');
          
          // Get product costs for all items
          const productCosts = new Map<string, number | null>();
          for (const item of currentItems) {
            try {
              const { data: product, error } = await supabase
                .from('products')
                .select('current_cost')
                .eq('id', item.productId)
                .single();
                
              if (!error && product) {
                productCosts.set(item.productId, product.current_cost);
              }
            } catch (err) {
              console.warn('Could not fetch cost for product:', item.productId, err);
              productCosts.set(item.productId, null);
            }
          }

          const itemsToInsert: PackageItemInsert[] = currentItems.map(item => ({
            company_id: companyId,
            package_id: packageId,
            product_id: item.productId,
            quantity: item.quantity,
            quoted_price_each: item.price,
            discount: 0,
            line_total_gross: item.price * item.quantity,
            line_total_net: item.price * item.quantity,
            cost_each_snapshot: productCosts.get(item.productId) || null,
          }));
          
          console.log('➕ Adding items:', itemsToInsert);
          await this.addPackageItems(itemsToInsert);
          console.log('✅ Successfully added', itemsToInsert.length, 'items');
        } catch (error) {
          console.error('❌ Failed to add items:', currentItems, error);
          throw new Error(`Failed to add package items: ${error.message}`);
        }
      }
      
      console.log('✅ Package items sync completed successfully');
      
    } catch (error) {
      console.error('❌ Error syncing package items:', error);
      throw error;
    }
  },
};

// Product search operations
export const productService = {
  // Search products by name or code with Turkish character support
  // Uses pagination to search through all products (handles >1000 rows)
  async searchProducts(companyId: string, searchTerm: string, limit: number = 20): Promise<ProductRow[]> {
    // Create multiple search patterns for Turkish characters
    const searchPatterns = [
      searchTerm,
      // Replace Turkish characters for better matching
      searchTerm.replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u'),
      searchTerm.replace(/c/g, 'ç').replace(/g/g, 'ğ').replace(/i/g, 'ı').replace(/o/g, 'ö').replace(/s/g, 'ş').replace(/u/g, 'ü')
    ];

    // Create multiple OR conditions for better matching
    const conditions = searchPatterns.flatMap(pattern => [
      `name.ilike.%${pattern}%`,
      `item_code.ilike.%${pattern}%`
    ]);

    const results: ProductRow[] = [];
    const pageSize = 1000; // Supabase max per query
    let offset = 0;
    let hasMore = true;

    // Fetch pages until we have enough results or no more data
    while (hasMore && results.length < limit) {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .or(conditions.join(','))
        .order('name')
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Error searching products:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      results.push(...data);

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    // Return only the requested limit
    return results.slice(0, limit);
  },

  // Get product by code
  async getProductByCode(companyId: string, itemCode: string): Promise<ProductRow | null> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('company_id', companyId)
      .eq('item_code', itemCode)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No rows found
      }
      console.error('Error fetching product by code:', error);
      throw error;
    }

    return data;
  },

  // Get all products for dropdown with pagination support
  async getProducts(companyId: string, limit: number = 100): Promise<ProductRow[]> {
    const results: ProductRow[] = [];
    const pageSize = 1000; // Supabase max per query
    let offset = 0;
    let hasMore = true;

    // Fetch pages until we have enough results or no more data
    while (hasMore && results.length < limit) {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name')
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Error fetching products:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      results.push(...data);

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    // Return only the requested limit
    return results.slice(0, limit);
  },

  // Get all products without limit (for full list pages)
  async getAllProducts(companyId: string, options?: {
    locationId?: string;
    showAllLocations?: boolean;
    includeInactive?: boolean;
  }): Promise<ProductRow[]> {
    const results: ProductRow[] = [];
    const pageSize = 1000; // Supabase max per query
    let offset = 0;
    let hasMore = true;

    // Fetch all pages
    while (hasMore) {
      let query = supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name')
        .range(offset, offset + pageSize - 1);

      // Apply location filter if provided and not showing all locations
      if (options?.locationId && !options?.showAllLocations) {
        query = query.eq('location_id', options.locationId);
      }

      // Filter out inactive products by default
      if (!options?.includeInactive) {
        query = query.is('exit_date', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching all products:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      results.push(...data);

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    return results;
  },
};

// Customer operations - customers are universal across all company locations
export const customerService = {
  // Search customers by name or phone with Turkish character support
  // Uses pagination to search through all customers (handles >1000 rows)
  async searchCustomers(companyId: string, searchTerm: string, limit: number = 20): Promise<CustomerRow[]> {
    // Create multiple search patterns for Turkish characters
    const searchPatterns = [
      searchTerm,
      // Replace Turkish characters for better matching
      searchTerm.replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u'),
      searchTerm.replace(/c/g, 'ç').replace(/g/g, 'ğ').replace(/i/g, 'ı').replace(/o/g, 'ö').replace(/s/g, 'ş').replace(/u/g, 'ü')
    ];

    // Create multiple OR conditions for better matching
    const conditions = searchPatterns.flatMap(pattern => [
      `first_name.ilike.%${pattern}%`,
      `last_name.ilike.%${pattern}%`,
      `phone.ilike.%${pattern}%`
    ]);

    const results: CustomerRow[] = [];
    const pageSize = 1000; // Supabase max per query
    let offset = 0;
    let hasMore = true;

    // Fetch pages until we have enough results or no more data
    while (hasMore && results.length < limit) {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .or(conditions.join(','))
        .order('first_name')
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Error searching customers:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      results.push(...data);

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    // Return only the requested limit
    return results.slice(0, limit);
  },

  // Get all customers for dropdown with pagination support
  async getCustomers(companyId: string, limit: number = 100): Promise<CustomerRow[]> {
    const results: CustomerRow[] = [];
    const pageSize = 1000; // Supabase max per query
    let offset = 0;
    let hasMore = true;

    // Fetch pages until we have enough results or no more data
    while (hasMore && results.length < limit) {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('first_name')
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Error fetching customers:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      results.push(...data);

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    // Return only the requested limit
    return results.slice(0, limit);
  },

  // Get all customers without limit (for full list pages)
  async getAllCustomers(companyId: string): Promise<CustomerRow[]> {
    const results: CustomerRow[] = [];
    const pageSize = 1000; // Supabase max per query
    let offset = 0;
    let hasMore = true;

    // Fetch all pages
    while (hasMore) {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('last_seen_at', { ascending: false, nullsLast: true })
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Error fetching all customers:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      results.push(...data);

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    return results;
  },
};

// Utility functions
export const packageUtils = {
  // Calculate package totals from items
  calculateTotals(items: Array<{ quantity: number; quoted_price_each: number | null; discount: number }>, globalDiscount: number = 0) {
    // Calculate subtotal after item-level discounts
    const subtotal = items.reduce((sum, item) => {
      const price = item.quoted_price_each || 0;
      const lineTotal = item.quantity * price;
      const lineDiscount = (lineTotal * item.discount) / 100;
      return sum + (lineTotal - lineDiscount);
    }, 0);

    // Calculate item-level discounts
    const itemDiscounts = items.reduce((sum, item) => {
      const price = item.quoted_price_each || 0;
      const lineTotal = item.quantity * price;
      return sum + (lineTotal * item.discount) / 100;
    }, 0);

    // Total discount includes both global discount and item-level discounts
    const totalDiscount = globalDiscount + itemDiscounts;

    // Gross total is the original total before any discounts
    // Since subtotal already has item discounts applied, we add item discounts back to get gross
    const totalGross = subtotal + itemDiscounts;

    return {
      total_gross: totalGross,
      total_discount: totalDiscount,
      total_net: subtotal - globalDiscount,
    };
  },

  // Format customer name
  formatCustomerName(customer: { first_name: string | null; last_name: string | null }): string {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown Customer';
  },

  // Format package status for display
  formatStatus(status: PackageStatus): { label: string; color: string } {
    const statusMap = {
      draft: { label: 'Taslak', color: 'bg-warning/20 text-warning-foreground border-warning/30' },
      offered: { label: 'Teklif Verildi', color: 'bg-blue/20 text-blue-foreground border-blue/30' },
      accepted: { label: 'Kabul Edildi', color: 'bg-success/20 text-success-foreground border-success/30' },
      rejected: { label: 'Reddedildi', color: 'bg-destructive/20 text-destructive-foreground border-destructive/30' },
      expired: { label: 'Süresi Doldu', color: 'bg-muted/20 text-muted-foreground border-muted/30' },
      converted: { label: 'Dönüştürüldü', color: 'bg-primary/20 text-primary-foreground border-primary/30' },
      cancelled: { label: 'İptal Edildi', color: 'bg-secondary/20 text-secondary-foreground border-secondary/30' },
    };
    
    return statusMap[status] || statusMap.draft;
  },
};
